/**
 * Weapon data — pickable weapons definitions.
 * Pure; no Phaser, no DOM imports.
 */

/** Discriminator for thrown/ranged weapon variants. */
export type ThrownWeaponKind = 'throwing-knife' | 'molotov' | 'brick';

export interface WeaponDef {
  readonly id: string;
  readonly label: string;
  /** Multiplier applied to base attack damage while weapon is equipped. */
  readonly damageMult: number;
  /** Visual placeholder colour (pending sprite art). */
  readonly color: number;
  /** Extra reach (px) beyond normal attack range. */
  readonly reachBonus: number;
  /** Number of swings before the weapon breaks. */
  readonly maxDurability: number;
  /**
   * Damage multiplier for a charged overhead attack (undefined = no charge).
   * Applied instead of `damageMult` on a held-heavy release.
   */
  readonly chargedDamageMult?: number;
  /** SFX key emitted when the charged attack fires (e.g. "ORDER! ORDER!"). */
  readonly chargedCallout?: string;

  // ---- Thrown / ranged weapon fields (undefined on melee weapons) ----

  /** Present on ranged weapons; identifies the projectile behaviour. */
  readonly thrownKind?: ThrownWeaponKind;
  /** Projectile travel speed in px/frame. */
  readonly projectileSpeed?: number;
  /** True = gravity arc (molotov, brick); false/undefined = straight (knife). */
  readonly projectileArc?: boolean;
  /** Max horizontal range for straight-line projectiles (px). */
  readonly projectileRange?: number;
  /** Duration (frames) of the fire patch spawned on landing. Molotov only. */
  readonly firePatchDuration?: number;
  /** Damage applied to each enemy overlapping the fire patch per tick. */
  readonly firePatchDamagePerTick?: number;
  /** Frames between consecutive fire-patch damage ticks. */
  readonly firePatchTickInterval?: number;
}

/** Mutable state of a weapon held by an entity. */
export interface WeaponState {
  readonly def: WeaponDef;
  durability: number;
}

export const WEAPON_PIPE: WeaponDef = {
  id: 'pipe',
  label: 'PIPE',
  damageMult: 1.5,
  color: 0x889298,
  reachBonus: 20,
  maxDurability: 5,
};

export const WEAPON_BAT: WeaponDef = {
  id: 'bat',
  label: 'BAT',
  damageMult: 1.75,
  color: 0xc8a45a,
  reachBonus: 28,
  maxDurability: 4,
};

export const WEAPON_KNIFE: WeaponDef = {
  id: 'knife',
  label: 'KNIFE',
  damageMult: 2.0,
  color: 0xdde0ee,
  reachBonus: 10,
  maxDurability: 3,
};

/**
 * The Praetor's shock baton — used in the Praetor's playable kit.
 * NOT the secret weapon; not included in WORLD_WEAPONS.
 */
export const WEAPON_SHOCK_BATON: WeaponDef = {
  id: 'shock-baton',
  label: 'SHOCK BATON',
  damageMult: 2.5,
  color: 0x4fc3f7,
  reachBonus: 24,
  maxDurability: 8,
};

/**
 * The Commons Mace — secret weapon unlocked at 10/14 endings for NG+ (MY-2002).
 * Ceremonial gold parliamentary mace: heavy wide swings, massive knockback.
 * Charged "ORDER!" overhead slam (`chargedDamageMult`) one-shots standard enemies;
 * `chargedCallout` triggers the "ORDER! ORDER!" audio sting.
 * NOT included in WORLD_WEAPONS (never spawns in normal runs).
 * Auto-granted in MANOR Mode; excluded from Score-Attack leaderboards.
 * Id matches SECRET_WEAPON_ID in rewardTiers.ts.
 */
export const WEAPON_COMMONS_MACE: WeaponDef = {
  id: 'commons-mace',
  label: 'THE COMMONS MACE',
  damageMult: 2.2,
  color: 0xffd700, // ceremonial gold
  reachBonus: 40,
  maxDurability: 99,
  chargedDamageMult: 9999, // ORDER! slam — one-shots standard enemies
  chargedCallout: 'sfx_order_callout',
};

/**
 * Throwing knife — fast straight projectile, one-use, picked up like a melee weapon.
 * Attack launches the projectile instead of a melee swing.
 */
export const THROWN_KNIFE: WeaponDef = {
  id: 'throwing-knife',
  label: 'THROWING KNIFE',
  damageMult: 1.8,
  color: 0xc0c8e0,
  reachBonus: 0,
  maxDurability: 1,
  thrownKind: 'throwing-knife',
  projectileSpeed: 14,
  projectileArc: false,
  projectileRange: 320,
};

/**
 * Molotov cocktail — arc trajectory; on landing, leaves a fire patch hazard
 * that deals periodic damage for several seconds.
 */
export const MOLOTOV: WeaponDef = {
  id: 'molotov',
  label: 'MOLOTOV',
  damageMult: 1.2,
  color: 0xff6600,
  reachBonus: 0,
  maxDurability: 1,
  thrownKind: 'molotov',
  projectileSpeed: 7,
  projectileArc: true,
  firePatchDuration: 180, // 3 s at 60 fps
  firePatchDamagePerTick: 4,
  firePatchTickInterval: 20, // tick every ~0.33 s
};

/**
 * Brick — arc trajectory, one-use blunt impact; no lingering hazard.
 */
export const BRICK: WeaponDef = {
  id: 'brick',
  label: 'BRICK',
  damageMult: 1.4,
  color: 0xb06040,
  reachBonus: 0,
  maxDurability: 1,
  thrownKind: 'brick',
  projectileSpeed: 9,
  projectileArc: true,
};

/** All weapons that can spawn in levels as world pickups. */
export const WORLD_WEAPONS: readonly WeaponDef[] = [
  WEAPON_PIPE,
  WEAPON_BAT,
  WEAPON_KNIFE,
  THROWN_KNIFE,
  MOLOTOV,
  BRICK,
];

/** Max horizontal distance to pick up a world weapon (px). */
export const PICKUP_RANGE = 80;

/** Distance from entity centre that a thrown weapon hitbox lands (px). */
export const WEAPON_THROW_RANGE = 160;
